import React from 'react';

const logoStyles = `
  @keyframes spinOuter {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes spinMiddle {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes bounceUp {
    0%   { transform: translateY(0px)  scale(1, 1);       }
    30%  { transform: translateY(-7px) scale(0.95, 1.08); }
    50%  { transform: translateY(-9px) scale(0.92, 1.1);  }
    70%  { transform: translateY(-7px) scale(0.95, 1.08); }
    85%  { transform: translateY(1px)  scale(1.06, 0.94); }
    100% { transform: translateY(0px)  scale(1, 1);       }
  }
  @keyframes shadowPulse {
    0%,100% { transform: scaleX(1);   opacity: 0.35; }
    50%     { transform: scaleX(0.5); opacity: 0.15; }
    85%     { transform: scaleX(1.2); opacity: 0.45; }
  }
`;

/**
 * AdjustD365 Animated Logo
 *
 * Props:
 *   size      — 'sm' | 'md' | 'lg'  (default: 'md')
 *   variant   — 'light' | 'dark' | 'blue' | 'navy'  (default: 'light')
 *   stacked   — show mark above wordmark (default: false = inline)
 *   showTagline — show "DYNAMICS 365 CONSULTING" tagline (default: false)
 */
export default function Logo({
  size = 'md',
  variant = 'light',
  stacked = false,
  showTagline = false,
  markOnly = false,
}) {
  const sizes = {
    sm: { mark: 36, wordmark: 22, gap: 10, tagline: 9 },
    md: { mark: 56, wordmark: 34, gap: 14, tagline: 10 },
    lg: { mark: 90, wordmark: 54, gap: 24, tagline: 11 },
  };
  const s = sizes[size];

  const colors = {
    light: {
      outerArc:  '#0078D4',
      middleArc: '#004578',
      innerFill: '#0078D4',
      ball:      '#ffffff',
      ballInner: '#0078D4',
      shadow:    'rgba(0,30,90,0.3)',
      word:      '#201F1E',
      tagline:   '#A0A0A0',
    },
    dark: {
      outerArc:  '#2899F5',
      middleArc: '#0078D4',
      innerFill: '#2899F5',
      ball:      '#ffffff',
      ballInner: '#2899F5',
      shadow:    'rgba(0,20,60,0.4)',
      word:      '#ffffff',
      tagline:   'rgba(255,255,255,0.3)',
    },
    blue: {
      outerArc:  'rgba(255,255,255,0.95)',
      middleArc: 'rgba(255,255,255,0.45)',
      innerFill: '#ffffff',
      ball:      '#0078D4',
      ballInner: 'rgba(255,255,255,0.6)',
      shadow:    'rgba(0,40,120,0.2)',
      word:      '#ffffff',
      tagline:   'rgba(255,255,255,0.4)',
    },
    navy: {
      outerArc:  'rgba(255,255,255,0.95)',
      middleArc: 'rgba(255,255,255,0.45)',
      innerFill: '#ffffff',
      ball:      '#004578',
      ballInner: 'rgba(255,255,255,0.6)',
      shadow:    'rgba(0,30,100,0.2)',
      word:      '#ffffff',
      tagline:   'rgba(255,255,255,0.35)',
    },
  };
  const c = colors[variant];

  const m  = s.mark;
  const cx = m / 2;
  const cy = m / 2;
  const r1 = m * 0.4;    // outer arc radius
  const r2 = m * 0.278;  // middle arc radius
  const r3 = m * 0.144;  // inner filled circle radius
  const rBall = m * 0.067; // bouncing ball radius
  const sw1 = m * 0.078;  // outer stroke width
  const sw2 = m * 0.061;  // middle stroke width

  // Dash arrays — ~290° for outer, ~250° for middle
  const c1 = 2 * Math.PI * r1;
  const c2 = 2 * Math.PI * r2;
  const da1 = `${(c1 * 0.805).toFixed(1)} ${c1.toFixed(1)}`;
  const da2 = `${(c2 * 0.70).toFixed(1)} ${c2.toFixed(1)}`;

  const AnimatedMark = () => (
    <svg
      width={m}
      height={m}
      viewBox={`0 0 ${m} ${m}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ overflow: 'visible', flexShrink: 0 }}
    >
      {/* Outer arc — slow clockwise */}
      <g style={{ transformOrigin: `${cx}px ${cy}px`, animation: 'spinOuter 8s linear infinite' }}>
        <circle cx={cx} cy={cy} r={r1} fill="none" stroke={c.outerArc} strokeWidth={sw1} strokeLinecap="round" strokeDasharray={da1} />
      </g>

      {/* Middle arc — faster clockwise */}
      <g style={{ transformOrigin: `${cx}px ${cy}px`, animation: 'spinMiddle 4.5s linear infinite' }}>
        <circle cx={cx} cy={cy} r={r2} fill="none" stroke={c.middleArc} strokeWidth={sw2} strokeLinecap="round" strokeDasharray={da2} />
      </g>

      {/* Inner filled circle */}
      <circle cx={cx} cy={cy} r={r3} fill={c.innerFill} />
      {/* Dome highlight arc */}
      <path
        d={`M ${cx - r3 * 0.6} ${cy - r3 * 0.35} Q ${cx} ${cy - r3 * 0.9} ${cx + r3 * 0.6} ${cy - r3 * 0.35}`}
        fill="none"
        stroke="rgba(255,255,255,0.3)"
        strokeWidth={m * 0.02}
        strokeLinecap="round"
      />

      {/* Shadow — squishes as ball rises */}
      <g style={{ transformOrigin: `${cx}px ${cy + r3 * 0.55}px` }}>
        <ellipse
          cx={cx}
          cy={cy + r3 * 0.55}
          rx={r3 * 0.55}
          ry={r3 * 0.18}
          fill={c.shadow}
          style={{ animation: 'shadowPulse 1.6s ease-in-out infinite' }}
        />
      </g>

      {/* Bouncing ball — pure vertical */}
      <circle
        cx={cx}
        cy={cy}
        r={rBall}
        fill={c.ball}
        style={{ animation: 'bounceUp 1.6s ease-in-out infinite', transformOrigin: `${cx}px ${cy}px` }}
      />
      {/* Specular highlight on ball */}
      <circle
        cx={cx - rBall * 0.42}
        cy={cy - rBall * 0.42}
        r={rBall * 0.32}
        fill="rgba(255,255,255,0.85)"
        style={{ animation: 'bounceUp 1.6s ease-in-out infinite', transformOrigin: `${cx}px ${cy}px` }}
      />
    </svg>
  );

  const Wordmark = () => (
    <div style={{
      display: 'flex',
      alignItems: 'baseline',
      fontSize: `${s.wordmark}px`,
      fontWeight: 700,
      letterSpacing: `${s.wordmark * -0.045}px`,
      lineHeight: 1,
      fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>
      <span style={{ color: c.word }}>Adjust</span>
      <span style={{ color: '#F25022' }}>D</span>
      <span style={{ color: '#7FBA00' }}>3</span>
      <span style={{ color: '#00A4EF' }}>6</span>
      <span style={{ color: '#FFB900' }}>5</span>
    </div>
  );

  if (markOnly) return (
    <>
      <style>{logoStyles}</style>
      <AnimatedMark />
    </>
  );

  return (
    <>
      <style>{logoStyles}</style>
      {stacked ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: s.gap * 0.8 }}>
          <AnimatedMark />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <Wordmark />
            {showTagline && (
              <div style={{ fontSize: s.tagline, fontWeight: 500, color: c.tagline, letterSpacing: '2.5px', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
                D365 CONSULTING
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: s.gap }}>
          <AnimatedMark />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Wordmark />
            {showTagline && (
              <div style={{ fontSize: s.tagline, fontWeight: 500, color: c.tagline, letterSpacing: '2.5px', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
                DYNAMICS 365 CONSULTING
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
